import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  SimpleGrid,
  Button,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverCloseButton,
} from '@chakra-ui/react';
import { SunIcon, MoonIcon } from '@chakra-ui/icons';
import { FaPalette } from 'react-icons/fa';
import { useTheme } from '../../hooks/useTheme';

const colorPalettes = [
  { name: 'gray', color: '#2D3748', value: 'gray' },
  { name: 'red', color: '#E53E3E', value: 'red' },
  { name: 'orange', color: '#DD6B20', value: 'orange' },
  { name: 'yellow', color: '#D69E2E', value: 'yellow' },
  { name: 'green', color: '#38A169', value: 'green' },
  { name: 'teal', color: '#319795', value: 'teal' },
  { name: 'blue', color: '#3182CE', value: 'blue' },
  { name: 'cyan', color: '#0BC5EA', value: 'cyan' },
  { name: 'purple', color: '#805AD5', value: 'purple' },
  { name: 'pink', color: '#D53F8C', value: 'pink' },
];

const fontFamilies = [
  { name: 'Inter', value: 'Inter, sans-serif', preview: 'Ag' },
  { name: 'Outfit', value: 'Outfit, sans-serif', preview: 'Ag' },
  { name: 'Geist', value: 'Geist, sans-serif', preview: 'Ag' },
  { name: 'Figtree', value: 'Figtree, sans-serif', preview: 'Ag' },
];

const borderRadiusOptions = [
  { name: 'none', value: '0', preview: '0px' },
  { name: 'xs', value: '0.125rem', preview: '2px' },
  { name: 'sm', value: '0.25rem', preview: '4px' },
  { name: 'md', value: '0.375rem', preview: '6px' },
  { name: 'lg', value: '0.5rem', preview: '8px' },
  { name: 'xl', value: '0.75rem', preview: '12px' },
  { name: '2xl', value: '1rem', preview: '16px' },
];

const fontSizeOptions = [
  { name: 'xs', value: 'xs', preview: '12px', size: '0.75rem' },
  { name: 'sm', value: 'sm', preview: '14px', size: '0.875rem' },
  { name: 'md', value: 'md', preview: '16px', size: '1rem' },
  { name: 'lg', value: 'lg', preview: '18px', size: '1.125rem' },
  { name: 'xl', value: 'xl', preview: '20px', size: '1.25rem' },
  { name: '2xl', value: '2xl', preview: '24px', size: '1.5rem' },
  { name: '3xl', value: '3xl', preview: '30px', size: '1.875rem' },
];

export const ThemePanel: React.FC = () => {
  const { themeConfig, updateThemeConfig, colorMode, toggleColorMode } = useTheme();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const selectedBorderColor = useColorModeValue('blue.500', 'blue.300');
  const hoverBorderColor = useColorModeValue('blue.500', 'blue.300');

  const ColorPaletteOption = ({ palette }: { palette: typeof colorPalettes[0] }) => (
    <Button
      size="lg"
      variant="outline"
      border="2px"
      borderColor={themeConfig.colorScheme === palette.value ? palette.color : borderColor}
      bg={bgColor}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        updateThemeConfig({ colorScheme: palette.value });
      }}
      _hover={{ borderColor: palette.color }}
      _focus={{ outline: 'none', borderColor: palette.color }}
      _focusVisible={{ outline: '2px solid', outlineColor: palette.color, outlineOffset: '2px' }}
      p={3}
      h="auto"
    >
      <Box
        w={6}
        h={6}
        borderRadius="full"
        bg={palette.color}
      />
    </Button>
  );

  const FontFamilyOption = ({ font }: { font: typeof fontFamilies[0] }) => (
    <Button
      size="lg"
      variant="outline"
      border="2px"
      borderColor={themeConfig.fontFamily === font.value ? selectedBorderColor : borderColor}
      bg={bgColor}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        updateThemeConfig({ fontFamily: font.value });
      }}
      _hover={{ borderColor: hoverBorderColor }}
      _focus={{ outline: 'none', borderColor: hoverBorderColor }}
      _focusVisible={{ outline: '2px solid', outlineColor: hoverBorderColor, outlineOffset: '2px' }}
      p={4}
      h="auto"
      flexDirection="column"
    >
      <Text fontSize="2xl" fontFamily={font.value} mb={1}>
        {font.preview}
      </Text>
      <Text fontSize="sm" color={mutedColor}>
        {font.name}
      </Text>
    </Button>
  );

  const RadiusOption = ({ radius }: { radius: typeof borderRadiusOptions[0] }) => (
    <Button
      size="lg"
      variant="outline"
      border="2px"
      borderColor={themeConfig.borderRadius === radius.name ? selectedBorderColor : borderColor}
      bg={bgColor}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        updateThemeConfig({ borderRadius: radius.name });
      }}
      _hover={{ borderColor: hoverBorderColor }}
      _focus={{ outline: 'none', borderColor: hoverBorderColor }}
      _focusVisible={{ outline: '2px solid', outlineColor: hoverBorderColor, outlineOffset: '2px' }}
      p={4}
      h="auto"
      flexDirection="column"
    >
      <Box
        w={8}
        h={8}
        bg="red.500"
        borderRadius={radius.value}
        mb={2}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          w="50%"
          h="100%"
          bg="red.300"
        />
      </Box>
      <Text fontSize="sm" color={mutedColor}>
        {radius.name}
      </Text>
    </Button>
  );

  const FontSizeOption = ({ fontSize }: { fontSize: typeof fontSizeOptions[0] }) => (
    <Button
      size="lg"
      variant="outline"
      border="2px"
      borderColor={themeConfig.fontSize === fontSize.value ? selectedBorderColor : borderColor}
      bg={bgColor}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        updateThemeConfig({ fontSize: fontSize.value });
      }}
      _hover={{ borderColor: hoverBorderColor }}
      _focus={{ outline: 'none', borderColor: hoverBorderColor }}
      _focusVisible={{ outline: '2px solid', outlineColor: hoverBorderColor, outlineOffset: '2px' }}
      p={4}
      h="auto"
      flexDirection="column"
    >
      <Text fontSize={fontSize.size} mb={1} color={textColor}>
        Aa
      </Text>
      <Text fontSize="sm" color={mutedColor}>
        {fontSize.name}
      </Text>
    </Button>
  );

  return (
    <Popover placement="bottom-end" closeOnBlur={false} closeOnEsc={true}>
      <PopoverTrigger>
        <IconButton
          aria-label="Theme settings"
          icon={<FaPalette />}
          variant="ghost"
          size="lg"
        />
      </PopoverTrigger>
      <PopoverContent w="400px" bg={bgColor} borderColor={borderColor} _focus={{ outline: 'none' }}>
        <PopoverHeader
          border="none"
          pb={3}
          pt={4}
          px={4}
        >
          <HStack justify="space-between" align="center">
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Theme Panel
            </Text>
            <IconButton
              aria-label="Toggle color mode"
              icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              variant="ghost"
              size="sm"
            />
          </HStack>
        </PopoverHeader>
        <PopoverCloseButton />
        <PopoverBody px={4} pb={4}>
          <VStack spacing={6} align="stretch">
            {/* Color Palette */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3} color={textColor}>
                Color Palette
              </Text>
              <SimpleGrid columns={6} spacing={2}>
                {colorPalettes.slice(0, 6).map((palette) => (
                  <ColorPaletteOption key={palette.value} palette={palette} />
                ))}
              </SimpleGrid>
              <SimpleGrid columns={4} spacing={2} mt={2}>
                {colorPalettes.slice(6).map((palette) => (
                  <ColorPaletteOption key={palette.value} palette={palette} />
                ))}
              </SimpleGrid>
            </Box>

            {/* Font Family */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3} color={textColor}>
                Font Family
              </Text>
              <SimpleGrid columns={2} spacing={2}>
                {fontFamilies.map((font) => (
                  <FontFamilyOption key={font.value} font={font} />
                ))}
              </SimpleGrid>
            </Box>

            {/* Font Size */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3} color={textColor}>
                Font Size
              </Text>
              <SimpleGrid columns={4} spacing={2}>
                {fontSizeOptions.slice(0, 4).map((fontSize) => (
                  <FontSizeOption key={fontSize.value} fontSize={fontSize} />
                ))}
              </SimpleGrid>
              <SimpleGrid columns={3} spacing={2} mt={2}>
                {fontSizeOptions.slice(4).map((fontSize) => (
                  <FontSizeOption key={fontSize.value} fontSize={fontSize} />
                ))}
              </SimpleGrid>
            </Box>

            {/* Border Radius */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3} color={textColor}>
                Radius
              </Text>
              <SimpleGrid columns={5} spacing={2}>
                {borderRadiusOptions.slice(0, 5).map((radius) => (
                  <RadiusOption key={radius.name} radius={radius} />
                ))}
              </SimpleGrid>
              <SimpleGrid columns={2} spacing={2} mt={2}>
                {borderRadiusOptions.slice(5).map((radius) => (
                  <RadiusOption key={radius.name} radius={radius} />
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};